import { type FormEvent, type ReactNode } from 'react'
import { Head, useForm } from '@inertiajs/react'
import { Check } from 'lucide-react'
import DashboardLayout from '@/components/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

type NotificationPreference = 'per_sale' | 'daily_digest'

interface NotificationsProps {
  notification_preference: NotificationPreference
}

const OPTIONS: Array<{
  value: NotificationPreference
  title: string
  description: string
}> = [
  {
    value: 'per_sale',
    title: 'Per sale',
    description: 'Get an email each time someone makes a purchase. Best for low volume.',
  },
  {
    value: 'daily_digest',
    title: 'Daily digest',
    description: 'Get one summary email each morning at 9am UTC with the previous day’s purchases.',
  },
]

export default function NotificationsShow({ notification_preference }: NotificationsProps) {
  const { data, setData, patch, processing, transform } = useForm({
    notification_preference,
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    transform(formData => ({ organization: formData }))
    patch('/dashboard/settings/notifications', { preserveScroll: true })
  }

  return (
    <>
      <Head title="Notifications" />
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="mb-8 text-4xl font-semibold tracking-tight">Notifications</h1>

        <Card>
          <CardHeader>
            <CardTitle>Purchase notifications</CardTitle>
            <CardDescription>
              Choose how you want to hear about new purchases on your events.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <fieldset
                className="grid grid-cols-1 gap-4 md:grid-cols-2"
                aria-label="Notification preference"
              >
                {OPTIONS.map(opt => {
                  const selected = data.notification_preference === opt.value
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setData('notification_preference', opt.value)}
                      aria-pressed={selected}
                      className={`relative rounded-xl border p-4 text-left transition ${
                        selected
                          ? 'border-primary bg-primary/5 ring-2 ring-primary'
                          : 'border-black/10 bg-card hover:border-primary/40'
                      }`}
                    >
                      {selected && (
                        <span className="absolute right-3 top-3 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
                          <Check className="h-4 w-4" />
                        </span>
                      )}
                      <p className="text-base font-semibold">{opt.title}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{opt.description}</p>
                    </button>
                  )
                })}
              </fieldset>

              <div className="flex justify-end pt-2">
                <Button type="submit" disabled={processing}>
                  Save
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

NotificationsShow.layout = (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>
